import { FieldArrayRenderProps, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Plus } from '@phosphor-icons/react';
import { useMemo } from 'react';

import { FormLabelStyled } from 'theSource/components/Form/components/FormField/FormFieldStyled';
import { InnerActionButtonStyled } from '../../AddEditContactFormStyled';
import { CheckboxField, Spacer, TextField } from 'theSource/components';
import { ContactFormGroupItem } from '../../types';

export interface GroupsFormSectionProps {
  arrayHelpers: FieldArrayRenderProps;
}

export const GroupsFormSection = ({ arrayHelpers }: GroupsFormSectionProps) => {
  const groups: ContactFormGroupItem[] = arrayHelpers.form.values.groups;
  const newGroupFieldName = 'nameYourGroup';
  const { t } = useTranslation('pages.contacts');

  const [
    { value: newGroupLabel },
    { error, touched },
    { setValue: setNewGroupLabelValue },
  ] = useField({
    name: newGroupFieldName,
  });

  const handleAddNewGroup = (): void => {
    if (!newGroupLabel || showExternalError) {
      return;
    }
    arrayHelpers.push({
      label: newGroupLabel,
      checked: true,
    });
    setNewGroupLabelValue('');
  };

  const showExternalError = useMemo(
    () =>
      Boolean(newGroupLabel) &&
      groups.filter((group) => group.label === newGroupLabel).length > 0,
    [groups, newGroupLabel]
  );

  const externalErrorMessage = useMemo(
    () => (showExternalError ? t('messages.contacts:duplicateGroupName') : ''),
    [showExternalError, t]
  );

  const isAddNewGroupButtonDisabled = useMemo(
    () => !newGroupLabel || (touched && Boolean(error)) || showExternalError,
    [newGroupLabel, touched, error, showExternalError]
  );

  return (
    <Box data-testid="contact-edit-groups-form-section">
      <FormLabelStyled labelFor={newGroupFieldName}>
        {t('assignContactTo')}
      </FormLabelStyled>
      <Spacer height={4} />
      {groups.map((group: ContactFormGroupItem, index: number) => (
        <CheckboxField
          key={index}
          label={group.label ?? ''}
          name={`groups[${index}].checked`}
          id={`groups[${index}].checked`}
        />
      ))}
      <Spacer height={2} />
      <TextField
        label=""
        id={newGroupFieldName}
        name={newGroupFieldName}
        placeholder={t('nameYourGroup')}
        externalError={showExternalError}
        helperText={externalErrorMessage}
      />
      <Spacer height={4} />
      <InnerActionButtonStyled
        data-testid="add-new-group-button"
        startIcon={<Plus size={16} weight="bold" />}
        label={t('addGroupButtonLabel')}
        variant="tertiary"
        onClick={handleAddNewGroup}
        disabled={isAddNewGroupButtonDisabled}
      />
    </Box>
  );
};
